import React from 'react';

import { Select, SelectOption } from '@sbu/components.select';

import {
  EntityCriticalityConfig as EntityCriticalitySelectConfig,
  EntityCriticalitySelectConfigsQuery as EntityCriticalitySelectConfigs,
} from 'core/graphql';
import { useQuery } from 'shared/hooks';

import css from './EntityCriticalitySelect.module.scss';
import { ENTITY_CRITICALITY_LISTS_SELECT_QUERY } from './queries';
import { DEFAULT_ENTITY_CRITICALITY } from './types';

export type EntityCriticalitySelectProps = {
  disabled?: boolean;
  loading?: boolean;
  value?: string | null;
  onValueChange: (value: string) => void;
};

export const EntityCriticalitySelect = ({
  disabled = false,
  loading = false,
  value = DEFAULT_ENTITY_CRITICALITY,
  onValueChange,
}: EntityCriticalitySelectProps) => {
  const { loading: queryLoading, data } =
    useQuery<EntityCriticalitySelectConfigs>(
      ENTITY_CRITICALITY_LISTS_SELECT_QUERY,
      { fetchPolicy: 'network-only' }
    );

  const entityCriticalityConfigs =
    data?.entityCriticalityConfigs?.objects ?? [];

  const criticalityConfigs = [
    ...entityCriticalityConfigs,
    {
      id: DEFAULT_ENTITY_CRITICALITY,
      name: DEFAULT_ENTITY_CRITICALITY,
      severityExpression: 'severity',
    },
  ];

  const getFormattedValue = ({
    name,
    severityExpression,
  }: EntityCriticalitySelectConfig) => `${name} (${severityExpression})`;

  const getInputValue = (value?: string | null) => {
    const matchingCriticalityConfig = criticalityConfigs.find(
      (criticalityConfig) => criticalityConfig.name === value
    );

    return matchingCriticalityConfig
      ? getFormattedValue(
          matchingCriticalityConfig as EntityCriticalitySelectConfig
        )
      : `${DEFAULT_ENTITY_CRITICALITY} (severity)`;
  };

  const onSelectChange = (value?: string | null) => {
    const matchingCriticalityConfig = criticalityConfigs.find(
      ({ name, severityExpression }) =>
        value === `${name} (${severityExpression})`
    );

    onValueChange(
      matchingCriticalityConfig?.name ?? DEFAULT_ENTITY_CRITICALITY
    );
  };

  return (
    <Select
      data-sumo-test='entity-criticality-select'
      disabled={disabled}
      emptyMessage='No matching criticality values...'
      fullWidth
      loading={loading || queryLoading}
      onChange={(e) => onSelectChange(e.target.value)}
      value={getInputValue(value)}
      SelectProps={{
        renderValue: (v) => <>{v}</>,
      }}
    >
      {criticalityConfigs.map(({ id, name, severityExpression }) => {
        const formattedValue = getFormattedValue({
          id,
          name,
          severityExpression,
        } as EntityCriticalitySelectConfig);

        return (
          <SelectOption key={id} value={formattedValue}>
            <div className={css.name}>{name}</div>
            <div className={css.severityExpression}>{severityExpression}</div>
          </SelectOption>
        );
      })}
    </Select>
  );
};
